import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useIntl } from '@edx/frontend-platform/i18n';
import { Form, StatefulButton } from '@edx/paragon';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router';

import ArrowButton from '../../common-components/ArrowButton';
import { PasswordField } from '../../common-components';
import PasswordStrength from '../../common-components/PasswordStrength';
import { validatePasswordField, validateConfirmPasswordField, passwordConditions } from '../../data/utils/passwordValidator';
import { getAllPossibleQueryParams, windowScrollTo } from '../../data/utils';
import { RESET_PAGE } from '../../data/constants';
import { resetPassword } from '../data/actions';
import { FORM_SUBMISSION_ERROR } from '../data/constants';
import messages from '../messages';

const PasswordSelection = ({
  setErrorCode,
  formErrors,
  setFormErrors,
  resetStatus,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useParams();

  const handlePasswordChange = (e) => {
    const { value } = e.target;
    setPassword(value);
    const validation = passwordConditions(value);
    const isValid = Object.values(validation).every(Boolean);
    setShowTooltip(!isValid);
  };

  const handleErrorChange = (fieldName, error) => {
    setFormErrors((prev) => ({ ...prev, [fieldName]: error }));
  };

  const hasValidationErrors = () => {
    return Object.values(formErrors).some((error) => error);
  };

  const handleOnFocus = (e) => {
    setFormErrors({ ...formErrors, [e.target.name]: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields on submit
    const passwordError = validatePasswordField(password, formatMessage);
    const confirmError = validateConfirmPasswordField(confirmPassword, password, formatMessage);

    // Set errors for any invalid fields
    if (passwordError) {
      setFormErrors((prev) => ({ ...prev, password: passwordError }));
    }
    if (confirmError) {
      setFormErrors((prev) => ({ ...prev, confirm_password: confirmError }));
    }

    // Only submit if no errors
    if (!passwordError && !confirmError) {
      const formPayload = {
        new_password1: password,
        new_password2: confirmPassword,
      };
      const params = getAllPossibleQueryParams();
      dispatch(resetPassword(formPayload, token, params));
    } else {
      setErrorCode(FORM_SUBMISSION_ERROR);
      windowScrollTo({ left: 0, top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Form id="set-reset-password-form" name="set-reset-password-form" className="d-flex justify-content-between flex-column flex-fill password-selection">
      <div className="position-relative flex-center-column gap-5 mb-4">
        <h1 className="page-title mb-5">{formatMessage(messages['reset.password'])}</h1>
        <p className="w-100 regular-text text-center mb-3">{formatMessage(messages['reset.password.page.instructions'])}</p>
        <div className="d-flex gap-20 password-fields-wrapper flex-column flex-md-row my-2">
          <PasswordField
            name="password"
            value={password}
            handleChange={handlePasswordChange}
            handleFocus={handleOnFocus}
            handleErrorChange={handleErrorChange}
            showErrorMessage
            alwaysShowLabel
            borderClass="bold-text"
            showStatusIcon={Boolean(password && confirmPassword)}
            passwordStatusSuccess={password === confirmPassword}
            placeholder={formatMessage(messages['choose.password.input.placeholder'])}
            absoluteLabel={formatMessage(messages['new.password.label'])}
            errorMessage={formErrors.password}
            showTooltip={showTooltip}
          />
          <div className="d-block d-md-none w-100">
            <PasswordStrength
              value={password}
              confirmPasswordValue={confirmPassword}
              style={{ width: '95%', marginStart: '0' }}
            />
          </div>
          <PasswordField
            name="confirm_password"
            value={confirmPassword}
            handleChange={(e) => setConfirmPassword(e.target.value)}
            handleFocus={handleOnFocus}
            handleErrorChange={handleErrorChange}
            mainPasswordValue={password}
            showErrorMessage
            alwaysShowLabel
            borderClass="bold-text"
            showStatusIcon={Boolean(password && confirmPassword)}
            passwordStatusSuccess={password === confirmPassword}
            placeholder={formatMessage(messages['verify.password.input.placeholder'])}
            absoluteLabel={formatMessage(messages['confirm.password.label'])}
            errorMessage={formErrors.confirm_password}
            showRequirements={false}
          />
        </div>
        <div className="d-none d-md-block w-100">
          <PasswordStrength
            value={password}
            confirmPasswordValue={confirmPassword}
            style={{ width: '95%', marginStart: '0' }}
          />
        </div>
      </div>
      <div className="buttons-wrapper w-100 flex-center-column my-5">
        <StatefulButton
          id="submit-new-password"
          name="submit-new-password"
          type="submit"
          className="logistration-btn"
          state={hasValidationErrors() ? 'disabled' : resetStatus}
          disabled={hasValidationErrors()}
          labels={{
            default: formatMessage(messages['reset.password.page.button.text']),
            pending: '',
          }}
          onClick={handleSubmit}
          onMouseDown={(e) => e.preventDefault()}
        />
        <ArrowButton className="w-100" onClick={() => navigate(RESET_PAGE)} />
      </div>
    </Form>
  );
};

PasswordSelection.defaultProps = {
  // errorCode: '',
};

PasswordSelection.propTypes = {
  // errorCode: PropTypes.string,
  setErrorCode: PropTypes.func.isRequired,
  formErrors: PropTypes.shape({
    password: PropTypes.string,
    confirmPassword: PropTypes.string,
  }).isRequired,
  setFormErrors: PropTypes.func.isRequired,
  resetStatus: PropTypes.string.isRequired,
};

export default PasswordSelection;
